import React, { useState, useMemo, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, InputGroup, Badge, ProgressBar, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUpload, FaPlus, FaTrash, FaSave, FaTimes, FaImage, FaChevronRight, FaChevronLeft, FaDollarSign, FaTag, FaInfoCircle, FaEye } from 'react-icons/fa';
import { FiType, FiFileText } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import Layout from '../../components/layout/Layout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import ProductCard from '../../components/ProductCard';
import './AddProduct.css';
import { useDropzone } from 'react-dropzone';

const CATEGORIES = ['Textbooks', 'Electronics', 'Stationery', 'Clothing', 'Furniture', 'Kitchen Appliances', 'Sports Equipment', 'Musical Instruments', 'Lab Equipment', 'Other'];
const CONDITIONS = ['New', 'Like New', 'Very Good', 'Good', 'Acceptable'];

const AddProduct = () => {
  const [step, setStep] = useState(1);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [productData, setProductData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    condition: 'New',
    images: [],
    features: [],
    stock: '',
  });

  const handleDescriptionChange = (value) => {
    setProductData((prev) => ({
      ...prev,
      description: value,
    }));
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({ 
    onDrop: (acceptedFiles) => {
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      const maxSizeMB = 5;

      for (const file of acceptedFiles) {
        if (!validImageTypes.includes(file.type)) {
          toast.error(`Unsupported file type: ${file.name}. Please use JPEG, PNG, GIF, or WEBP.`);
          return;
        }
        if (file.size > maxSizeMB * 1024 * 1024) {
          toast.error(`File ${file.name} exceeds the ${maxSizeMB}MB size limit.`);
          return;
        }
      }

      // Store the file objects directly, along with a preview URL
      const newImages = acceptedFiles.map(file => Object.assign(file, {
        preview: URL.createObjectURL(file)
      }));
      setProductData(prev => ({ ...prev, images: [...prev.images, ...newImages] }));
    }, 
    accept: 'image/*', 
    maxFiles: 4, 
    maxSize: 5 * 1024 * 1024, 
    noClick: true, // We'll trigger it manually
    noKeyboard: true,
  });

  const MAX_NAME_LENGTH = 100;
  const MAX_DESC_LENGTH = 1000;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProductData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...productData.features];
    // Ensure the value is never undefined
    newFeatures[index] = value || '';
    setProductData({ ...productData, features: newFeatures });
  };

  const addFeatureField = () => {
    setProductData((prev) => ({ ...prev, features: [...prev.features, ''] }));
  };

  const removeFeatureField = (index) => {
    const updatedFeatures = productData.features.filter((_, i) => i !== index);
    setProductData((prev) => ({ ...prev, features: updatedFeatures }));
  };

  const removeImage = (indexToRemove) => {
    setProductData(prev => {
      // Revoke the object URL to avoid memory leaks
      URL.revokeObjectURL(prev.images[indexToRemove].preview);
      return {
        ...prev,
        images: prev.images.filter((_, index) => index !== indexToRemove),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productData.name || !productData.price || !productData.category || !productData.condition || productData.images.length === 0) {
      toast.error('Please fill all required fields and upload at least one image.');
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append('name', productData.name);
    formData.append('description', productData.description);
    formData.append('price', productData.price);
    formData.append('category', productData.category);
    formData.append('condition', productData.condition);
    
    // Append features as a JSON string
    formData.append('features', JSON.stringify(productData.features.filter(f => f)));

    // Append each image file
    productData.images.forEach(imageFile => {
      formData.append('images', imageFile);
    });

    formData.append('stock', productData.stock);

    // Fix: send condition in backend format (lowercase, underscores)
    formData.set('condition', productData.condition.toLowerCase().replace(/\s+/g, '_'));

    try {
      // Get the token from localStorage
      const token = localStorage.getItem('token');

      await api.post('/products/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}` // Add the auth token
        },
      });
      toast.success('Product submitted for approval. It will be listed once approved.');
      navigate('/seller/products');
    } catch (err) {
      toast.error(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const STEPS = [
    { number: 1, title: 'Product Information' },
    { number: 2, title: 'Media' },
    { number: 3, title: 'Pricing & Details' },
    { number: 4, title: 'Review & Submit' },
  ];

  const progress = Math.round(((step - 1) / (STEPS.length -1)) * 100);

  const nextStep = () => setStep(prev => Math.min(prev + 1, STEPS.length));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

  const PriceAnalysis = ({ price }) => {
    const platformFeeRate = 0.035; // 3.5% fee
    const fee = useMemo(() => (price * platformFeeRate).toFixed(2), [price]);
    const earnings = useMemo(() => (price - fee).toFixed(2), [price, fee]);

    if (!price || price <= 0) return null;

    return (
      <div className="price-analysis-box">
        <div className="price-analysis-item">
          <span><FaTag className="me-2 text-muted"/>Platform Fee (3.5%)</span>
          <strong>- GH₵{fee}</strong>
        </div>
        <div className="price-analysis-item final-earning">
          <span><FaDollarSign className="me-2 text-success"/>Your Potential Earnings</span>
          <strong>GH₵{earnings}</strong>
        </div>
      </div>
    );
  };



  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: { duration: 0.2 }
    }
  };

  return (
    <Layout>
      <Container className="add-product-page">
        <motion.div 
          className="wizard-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2>Create a New Listing</h2>
          <p>Follow the steps to get your product listed on the marketplace.</p>
        </motion.div>

        <Row>
          <Col lg={10} xl={8} className="mx-auto">
            {/* Progress Stepper */}
            <motion.div 
              className="stepper-container mb-5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {STEPS.map(({ number, title }) => (
                <React.Fragment key={number}>
                  <div className={`step-item ${step >= number ? 'active' : ''}`}>
                    <div className="step-circle">{number}</div>
                    <div className="step-title">{title}</div>
                  </div>
                  {number < STEPS.length && <div className="step-connector" />}
                </React.Fragment>
              ))}
              <div className="stepper-progress-bar">
                <ProgressBar now={progress} />
              </div>
            </motion.div>

            <Form onSubmit={handleSubmit}>
              <AnimatePresence mode="wait">
                {/* Step 1: Product Information */}
                {step === 1 && (
                  <motion.div
                    key="step1"
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <Card className="form-section-card">
                  <Card.Header>Product Information</Card.Header>
                  <Card.Body>
                    <Form.Group as={Col} md={12} controlId="productName">
                      <Form.Label>Product Name</Form.Label>
                      <div className="input-with-icon">
                        <div className="input-icon-container">
                          <FiType className="input-icon" />
                        </div>
                        <Form.Control
                          type="text"
                          name="name"
                          placeholder="e.g., 'Introduction to Psychology', 'iPhone 12 Pro Max'"
                          value={productData.name}
                          onChange={handleChange}
                          maxLength={MAX_NAME_LENGTH}
                          required
                        />
                        <span className="char-counter">{productData.name.length}/{MAX_NAME_LENGTH}</span>
                      </div>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="productDescription">
                      <Form.Label>Description</Form.Label>
                      <ReactQuill
                        theme="snow"
                        value={productData.description}
                        onChange={handleDescriptionChange}
                        placeholder="Provide a detailed description of your product, including its features, condition, and any included accessories."
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, false] }],
                            ['bold', 'italic', 'underline'],
                            [{'list': 'ordered'}, {'list': 'bullet'}],
                            ['clean']
                          ],
                        }}
                      />
                      <Form.Text className="text-muted d-flex justify-content-end mt-1">
                        {/* Character count for Quill is more complex, so we'll omit it for now */}
                      </Form.Text>
                    </Form.Group>
                    </Card.Body>
                  </Card>
                </motion.div>
              )}

              {/* Step 2: Media */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <Card className="form-section-card">
                  <Card.Header>Upload Images</Card.Header>
                  <Card.Body>
                    <div {...getRootProps({
                      className: `image-drop-zone ${isDragActive ? 'is-drag-active' : ''}`
                    })}>
                      <input {...getInputProps()} />
                      <div className="text-center">
                        <FaUpload size={40} className="text-muted mb-3" />
                        <h4>Drag 'n' drop images here</h4>
                        <p className="text-muted">or</p>
                        <Button variant="primary" onClick={open}>Select Files</Button>
                        <p className="text-muted mt-2">(Up to 4 images, 5MB each)</p>
                      </div>
                    </div>
                    {productData.images.length > 0 && (
                      <div className="image-preview-container mt-4">
                        <h5 className="mb-3">Image Previews</h5>
                        <Row xs={2} md={3} lg={4} className="g-3">
                          {productData.images.map((img, index) => (
                            <Col key={index}>
                              <div className="image-preview-item">
                                <img src={img.preview} alt={`Preview ${index + 1}`} className="img-thumbnail" />
                                <Button variant="danger" size="sm" className="remove-image-btn" onClick={() => removeImage(index)}><FaTimes /></Button>
                                {index === 0 && <Badge bg="primary" className="main-image-badge">Main</Badge>}
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}
                  </Card.Body>
                  </Card>
                </motion.div>
              )}

              {/* Step 3: Pricing & Details */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <Card className="form-section-card">
                  <Card.Header>Pricing & Details</Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-4">
                          <Form.Label>Price (GH₵)</Form.Label>
                          <InputGroup>
                            <InputGroup.Text>GH₵</InputGroup.Text>
                            <Form.Control type="number" name="price" step="0.01" min="0" value={productData.price} onChange={handleChange} required />
                          </InputGroup>
                          <PriceAnalysis price={productData.price} />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-4">
                          <Form.Label>Items in Stock</Form.Label>
                          <Form.Control type="number" name="stock" min="1" value={productData.stock} onChange={handleChange} required />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-4">
                          <Form.Label>Category</Form.Label>
                          <Form.Select name="category" value={productData.category} onChange={handleChange} required>
                            <option value="">Select...</option>
                            {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-4">
                          <Form.Label>Condition</Form.Label>
                          <Form.Select name="condition" value={productData.condition} onChange={handleChange} required>
                            <option value="">Select...</option>
                            {CONDITIONS.map(con => <option key={con} value={con}>{con}</option>)}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <hr className="my-4"/>
                    <h5 className="mb-3">Features (Optional)</h5>
                    {productData.features.map((feature, index) => (
                      <InputGroup className="mb-3 feature-input-group" key={index}>
                        <Form.Control type="text" placeholder={`Feature ${index + 1}`} value={feature} onChange={(e) => handleFeatureChange(index, e.target.value)} />
                        <Button variant="outline-danger" onClick={() => removeFeatureField(index)}><FaTrash /></Button>
                      </InputGroup>
                    ))}
                    <Button variant="outline-primary" onClick={addFeatureField}><FaPlus className="me-2" />Add Feature</Button>
                  </Card.Body>
                  </Card>
                </motion.div>
              )}

              {/* Step 4: Review & Submit */}
              {step === 4 && (
                <motion.div
                  key="step4"
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <Card className="form-section-card">
                  <Card.Header>Review & Submit</Card.Header>
                  <Card.Body className="text-center">
                    <FaInfoCircle size={40} className="text-primary mb-3"/>
                    <h4>You're All Set!</h4>
                    <p className="text-muted">Review your product details in the live preview on the right. If everything looks good, submit your listing.</p>
                    <Button type="submit" variant="primary" size="lg" disabled={loading} className="mt-3">
                      <FaSave className="me-2" />
                      {loading ? 'Submitting...' : 'Submit Listing'}
                    </Button>
                  </Card.Body>
                  </Card>
                </motion.div>
              )}
              </AnimatePresence>
            </Form>

            {/* Navigation Buttons */}
            <div className="d-flex justify-content-between align-items-center mt-4">
              <div>
                {step > 1 && (
                  <Button variant="outline-secondary" onClick={prevStep}>
                    <FaChevronLeft className="me-2"/> Back
                  </Button>
                )}
              </div>

              <Button variant="secondary" onClick={() => setShowPreviewModal(true)}>
                <FaEye className="me-2" /> Preview
              </Button>
              {step < STEPS.length ? (
                <Button variant="primary" onClick={nextStep}>
                  Next <FaChevronRight className="ms-2"/>
                </Button>
              ) : null}
            </div>
          </Col>


        </Row>
      </Container>

      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Product Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light text-center p-4">
          <p className="text-muted">This is how your product will appear on the marketplace.</p>
          <div className="d-inline-block">
              <ProductCard 
              product={{
                ...productData,
                images: productData.images.length > 0 ? productData.images.map(f => f.preview) : ['https://placehold.co/600x400?text=Your+Image+Here'],
                seller: {
                  name: currentUser.name || 'Your Name',
                  profilePicture: currentUser.profilePicture || `https://i.pravatar.cc/150?u=${currentUser.uid}`,
                  userType: currentUser.userType || 'student',
                },
                rating: { average: 0, count: 0 },
              }}
              isPreview={true} 
            />
          </div>
        </Modal.Body>
      </Modal>

    </Layout>
  );
};

export default AddProduct;
